// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { ChartOutput, TextWidgetType, WidgetOutput, WidgetSeriesOutput } from 'thehive-sdk';
import { WidgetError } from '@Organisms/Dashboard/DashboardDetails/WidgetError';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { ActionWidget } from './ActionWidget';
import * as htmlToImage from 'html-to-image';
import { downloadFile } from '@Helpers/download';
import { WidgetLoading } from '@Organisms/Dashboard/DashboardDetails/WidgetLoading/WidgetLoading';
import { UseQueryResult } from 'react-query';

import './TextWidget.scss';

export interface TextChartOutput {
    name: string;
    value: string | number;
}

const prepareChartData = (lineData?: ChartOutput, series?: WidgetSeriesOutput[]): TextChartOutput[] => {
    return Object.entries(lineData || {}).map(([label, value], index) => ({
        name: series?.[index].name || series?.[index].label || label,
        value,
    }));
};

interface TextWidgetProps {
    widget: WidgetOutput;
    query?: UseQueryResult<ChartOutput>;
}

export const TextWidget = React.memo(({ widget, query }: TextWidgetProps): ReactElement => {
    const options = widget.options as TextWidgetType;
    const { template, series = [] } = options;

    const [text, setText] = useState(template);

    const dataTreated = useMemo<TextChartOutput[]>(
        () => prepareChartData(query?.data, series) || [],
        [query?.data, series],
    );

    useEffect(() => {
        setText(template);
        dataTreated.forEach(({ name, value }) =>
            setText((prevValue) => prevValue.replaceAll(`{{${name}}}`, `${value}`)),
        );
    }, [dataTreated, template]);

    if (query?.isLoading) {
        return (
            <div className="widget__empty">
                <WidgetLoading />
            </div>
        );
    }

    if (query?.status === 'error') {
        return <WidgetError />;
    }

    return (
        <div className="widget__display">
            <div className="text-widget" id={`text-widget-${widget.id}`}>
                <THMarkdown value={text} borderless disabled className="text-widget__markdown" />
            </div>

            <ActionWidget
                onDownloadImage={() => {
                    const element = document.getElementById(`text-widget-${widget.id}`);

                    if (!element) {
                        return;
                    }

                    htmlToImage
                        .toPng(element, { backgroundColor: 'transparent' })
                        .then((file) => downloadFile(widget.options.title || 'widget', file, 'image/png', true));
                }}
                onDownloadTXT={() => {
                    downloadFile((widget.options.title || 'widget') + '.md', text, 'text/markdown');
                }}
            />
        </div>
    );
});
